import { ApiClient } from './api-client';
import { LoggingService } from '../services/logging-service';
import { Observable, throwError } from 'rxjs';
import { Endpoints } from './endpoints';
import { catchError } from 'rxjs/operators';
import { BsError } from '../models/shared/bs-error';
import { ApiErrorLog } from '../models/shared/api-error-log';
import { Injectable } from '@angular/core';
import { Menu } from '../models/menu/menu';
import { Display } from '../models/display/dto/display';
import { WebSocketService } from '../services/web-socket.service';
import { WebSocketRequestTemplate } from '../models/web-sockets/messages/send/web-socket-request-template';
import { PartialMenuRequest } from '../models/menu/dto/partial-menu-request';

@Injectable({ providedIn: 'root' })
export class TemplateAPI {
  constructor(
    private apiClient: ApiClient,
    private loggingService: LoggingService,
    private webSocketService: WebSocketService
  ) {}

  public serviceName = 'Template';

  public getMenuTemplateForLocation$(locationId: number, menuTemplateId: string): Observable<Menu> {
    return this.webSocketGetMenuTemplateForLocation$(locationId, menuTemplateId);
  }

  private webSocketGetMenuTemplateForLocation$(locationId: number, templateId: string): Observable<Menu> {
    this.webSocketService.connectToLocationId(locationId);
    this.webSocketService.connectToTemplateId(templateId);
    const msg = new WebSocketRequestTemplate(locationId, templateId);
    return this.webSocketService.requestObject$(msg, Menu).pipe(
      catchError(error => {
        const err = new BsError(error, this.serviceName);
        const logErr = new ApiErrorLog(this.serviceName, 'GetMenuTemplateForLocationFromWebSocket', err);
        this.loggingService.LogAPIError(logErr);
        return this.restfulGetMenuTemplateForLocation$(locationId, templateId);
      })
    );
  }

  public restfulGetMenuTemplateForLocation$(locationId: number, menuTemplateId: string): Observable<Menu> {
    const url = Endpoints.GetMenuTemplate(locationId, menuTemplateId);
    return this.apiClient.getObj$(Menu, url).pipe(
      catchError(e => {
        const err = new BsError(e, this.serviceName);
        this.loggingService.LogAPIError(new ApiErrorLog(this.serviceName, 'GetMenuTemplate', err));
        return throwError(() => err);
      })
    );
  }

  public getPartialMenuTemplate$(locationId: number, menuTemplateId: string, variantIds: string[]): Observable<Menu> {
    const url = Endpoints.GetPartialMenuTemplate(locationId, menuTemplateId);
    const requestBody = new PartialMenuRequest(variantIds, []);
    return this.apiClient.postObj$(Menu, url, requestBody).pipe(
      catchError(e => {
        const err = new BsError(e, this.serviceName);
        this.loggingService.LogAPIError(new ApiErrorLog(this.serviceName, 'GetPartialMenuTemplate', err));
        return throwError(err);
      })
    );
  }

  public getPartialShelfTalkerTemplate$(
    locationId: number,
    menuTemplateId: string,
    sectionIds: string[]
  ): Observable<Menu> {
    const url = Endpoints.GetPartialMenuTemplate(locationId, menuTemplateId);
    const requestBody = new PartialMenuRequest([], sectionIds);
    return this.apiClient.postObj$(Menu, url, requestBody).pipe(
      catchError(e => {
        const err = new BsError(e, this.serviceName);
        this.loggingService.LogAPIError(new ApiErrorLog(this.serviceName, 'GetPartialShelfTalkerTemplate', err));
        return throwError(err);
      })
    );
  }

  public getTemplateCollection$(locationId: number, collectionId: string): Observable<Display> {
    const url = Endpoints.GetTemplateCollection(locationId, collectionId);
    return this.apiClient.getObj$(Display, url).pipe(
      catchError(e => {
        const err = new BsError(e, this.serviceName);
        this.loggingService.LogAPIError(new ApiErrorLog(this.serviceName, 'GetTemplateCollection', err));
        return throwError(err);
      })
    );
  }
}
